/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module SecurityModule
 */

/**
 * @description
 *
 *   SSLKeyParams config item
 *
 * @author Nisar Nadaf
 */
import { MessageItem } from 'ajs/modules/data-model/factories/message-item.factory';

import {
    ISSLKeyECParams,
    ISSLKeyMldsaParams,
    ISSLKeyParams,
    ISSLKeyRSAParams,
    SSLKeyAlgorithm,
    SSLKeyECCurve,
    SSLKeyMldsaAlgorithm,
    SSLRSAKeySize,
} from 'generated-types';

type TSSLKeyParamsPartial = Omit<ISSLKeyParams, 'ec_params' | 'rsa_params' | 'mldsa_params'>;

interface ISSLKeyParamsConfig extends TSSLKeyParamsPartial {
    ec_params: MessageItem<ISSLKeyECParams>;
    rsa_params: MessageItem<ISSLKeyRSAParams>;
    mldsa_params: MessageItem<ISSLKeyMldsaParams>;
}

/**
 * Ajs dependency token for SSLKeyParamsConfigItem.
 */
export const SSL_KEY_PARAMS_CONFIG_ITEM_TOKEN = 'SSLKeyParamsConfigItem';

export class SSLKeyParamsConfigItem extends MessageItem<ISSLKeyParamsConfig> {
    constructor(args = {}) {
        const extendedArgs = {
            objectType: 'SSLKeyParams',
            ...args,
        };

        super(extendedArgs);
    }

    /**
     * Getter for algorithm.
     */
    public get algorithm(): SSLKeyAlgorithm {
        const { algorithm } = this.config;

        return algorithm;
    }

    /**
     * Getter for key_size of rsa_params.
     */
    public get keySize(): SSLRSAKeySize {
        const { rsa_params: { config: rsaParamsConfig } } = this.config;
        const { key_size: keySize } = rsaParamsConfig;

        return keySize;
    }

    /**
     * Getter for curve of ec_params.
     */
    public get curve(): SSLKeyECCurve {
        const { ec_params: { config: ecParamsConfig } } = this.config;
        const { curve } = ecParamsConfig;

        return curve;
    }

    /**
     * Getter for algorithm of mldsa_params.
     */
    public get mldsaAlgorithm(): SSLKeyMldsaAlgorithm {
        const { mldsa_params: { config: mldsaParamsConfig } } = this.config;
        const { algorithm } = mldsaParamsConfig;

        return algorithm;
    }

    /**
     * Handle SSL key algorithm change to ensure necessary parameters exist.
     */
    public onAlgorithmChange(algorithm: SSLKeyAlgorithm): void {
        if (algorithm === SSLKeyAlgorithm.SSL_KEY_ALGORITHM_MLDSA) {
            if (!this.config.mldsa_params) {
                const mldsaParams = this.createChildByField('mldsa_params', {
                    algorithm: SSLKeyMldsaAlgorithm.SSL_KEY_MLDSA44,
                }) as MessageItem<ISSLKeyMldsaParams>;

                this.config.mldsa_params = mldsaParams;
            }
        }
    }
}
